import { ORDER_STATUS } from 'src/constants/order';
import { LABEL } from 'src/utils/status/order/constants';
import { BADGE_TYPES } from 'src/components/Elements/Badge';

const FUTURE_STATUS_IDS = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.SEARCHING,
  ORDER_STATUS.ASSIGNED,
  ORDER_STATUS.PAUSED,
  ORDER_STATUS.READY_TO_RESUME,
  ORDER_STATUS.PENDING_SHIPPING,
];

/**
 * The last order in the workflow should represent the entire workflow so that we can
 * display the workflow as one card.
 */
export const constructWorkflowGroups = (workflowGroupsObj) => {
  return Object.entries(workflowGroupsObj).map(([key, workflowOrders]) => {
    const lastOrderInGroup = workflowOrders.reduce((prev, current) =>
      prev.order.id > current.order.id ? prev : current,
    );
    return {
      id: key,
      name: key,
      order: { ...lastOrderInGroup.order },
      isWorkflowOrder: true,
    };
  });
};

export const processOrders = (orders) => {
  /** Orders grouped by workflow token */
  const workflowOrdersMap = {};
  /** Standdalone future orders */
  const futureOrders = [];
  /** Standalone past orders */
  const pastOrders = [];

  if (!orders) {
    return { futureOrders, pastOrders };
  }

  const isFutureOrder = (order) =>
    FUTURE_STATUS_IDS.includes(order.statusId) || order.statusId === ORDER_STATUS.LATE_TECH;

  // Separate orders into workflow orders and standalone orders
  orders.forEach((order) => {
    const isOrderPartOfWorkflow = Boolean(order.workflow);
    if (isOrderPartOfWorkflow) {
      const workflowToken = order.workflow.token;
      workflowOrdersMap[workflowToken] = workflowOrdersMap[workflowToken] || [];
      workflowOrdersMap[workflowToken].push({ order });
    } else if (isFutureOrder(order)) {
      futureOrders.push({ order });
    } else {
      pastOrders.push({ order, isPastOrder: true });
    }
  });

  const workflowGroups = constructWorkflowGroups(workflowOrdersMap);

  // Categorize each workflow group as future or past. The group is represented by the last order in the group
  workflowGroups.forEach((group) => {
    const isFutureWorkflow = isFutureOrder(group.order);
    if (isFutureWorkflow) {
      futureOrders.push(group);
    } else {
      pastOrders.push(group);
    }
  });

  // Sort the future and past orders arrays in descending order
  futureOrders.sort((a, b) => new Date(b.order.createdAt) - new Date(a.order.createdAt));
  pastOrders.sort((a, b) => new Date(b.order.createdAt) - new Date(a.order.createdAt));

  return { futureOrders, pastOrders };
};

/**
 * Gets the badge status configuration based on the order.
 *
 * @param {Order} order - The order object.
 * @param {{type: string, variant?: string}} badgeType - The default badge type.
 * @param {string} label - The default label.
 */ export const getBadgeStatusConfig = (order, badgeType, label) => {
  if (!order) {
    return { statusBadgeType: badgeType, statusLabel: label };
  }
  if (order.hasAuthError) {
    return {
      statusLabel: LABEL.ACTION_REQUIRED,
      statusBadgeType: { type: BADGE_TYPES.WARNING },
    };
  }

  return { statusBadgeType: badgeType, statusLabel: label };
};

/** @typedef {import ('src/types/order').Order} Order */
